import { meterToString } from '@tunasong/models'
import type { Meter } from '@tunasong/models'
import { ScaleLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'

export interface ScalarFieldChange {
  label: string
  from: string
  to: string
}

export interface TagsFieldChange {
  label: 'Tags'
  added: string[]
  removed: string[]
}

export type FieldChange = ScalarFieldChange | TagsFieldChange

export function isTagsFieldChange(change: FieldChange): change is TagsFieldChange {
  return 'added' in change
}

interface VersionFields {
  [key: string]: unknown
  name?: unknown
  tags?: unknown
  scale?: unknown
  rhythm?: unknown
}

const none = '—'

const rhythmOf = (fields: VersionFields) =>
  (fields.rhythm && typeof fields.rhythm === 'object' ? fields.rhythm : {}) as { tempo?: unknown; meter?: unknown }

const nameOf = (fields: VersionFields) => (typeof fields.name === 'string' && fields.name ? fields.name : none)

const keyOf = (fields: VersionFields) =>
  fields.scale && typeof fields.scale === 'object' ? ScaleLib.getName(fields.scale as Scale) : none

const tempoOf = (fields: VersionFields) => {
  const { tempo } = rhythmOf(fields)
  return typeof tempo === 'number' ? String(tempo) : none
}

const meterOf = (fields: VersionFields) => {
  const { meter } = rhythmOf(fields)
  return (meter && typeof meter === 'object' ? meterToString(meter as Meter) : null) ?? none
}

const tagsOf = (fields: VersionFields) =>
  Array.isArray(fields.tags) ? fields.tags.filter((tag): tag is string => typeof tag === 'string') : []

/** The scalar fields people recognise (GH #2695 spec §3), in the order they are listed. Internal fields never show. */
const scalarFields: [string, (fields: VersionFields) => string][] = [
  ['Name', nameOf],
  ['Key', keyOf],
  ['Tempo', tempoOf],
  ['Meter', meterOf],
]

/** The field changes from one version to the next: name, tags, key, tempo and meter */
export const versionFieldChanges = (before: VersionFields, after: VersionFields): FieldChange[] => {
  const scalars = scalarFields
    .map(([label, read]) => ({ label, from: read(before), to: read(after) }))
    .filter(change => change.from !== change.to)

  const beforeTags = tagsOf(before)
  const afterTags = tagsOf(after)
  const added = afterTags.filter(tag => !beforeTags.includes(tag))
  const removed = beforeTags.filter(tag => !afterTags.includes(tag))
  const tags: FieldChange[] = added.length || removed.length ? [{ label: 'Tags', added, removed }] : []

  /** Name, then Tags, then the musical fields */
  return [...scalars.filter(c => c.label === 'Name'), ...tags, ...scalars.filter(c => c.label !== 'Name')]
}
